import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, orderBy, doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebaseconfig';
import { useAuth } from '../../contexts/AuthContext';
import './Savings.css';

const StaffSavings = () => {
  const [savings, setSavings] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { currentUser } = useAuth();

  useEffect(() => {
    if (currentUser) {
      fetchStaffSavings();
      fetchTransactions();
    }
  }, [currentUser]);

  const fetchStaffSavings = async () => {
    try {
      const savingsRef = doc(collection(db, 'staff_savings'), currentUser.uid);
      const savingsDoc = await getDoc(savingsRef);
      
      if (savingsDoc.exists()) {
        setSavings(savingsDoc.data());
      } else {
        setError('No savings account found');
      }
    } catch (err) {
      console.error('Error fetching savings:', err);
      setError('Failed to load savings data');
    }
  };

  const fetchTransactions = async () => {
    try {
      const transactionsRef = collection(db, 'savings');
      const q = query(
        transactionsRef,
        where('userId', '==', currentUser.uid),
        orderBy('createdAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      const transactionsList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date()
      }));

      setTransactions(transactionsList);
    } catch (err) {
      console.error('Error fetching transactions:', err);
      setError('Failed to load transaction history');
    } finally {
      setLoading(false);
    }
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN'
    }).format(amount);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-NG', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTransactionLabel = (type) => {
    const labels = {
      deposit: 'Deposit',
      withdrawal: 'Withdrawal',
      transfer_in: 'Transfer Received',
      transfer_out: 'Transfer Sent'
    };
    return labels[type] || type;
  };

  if (loading) {
    return <div className="loading">Loading savings data...</div>;
  }

  if (error) {
    return <div className="error-message">{error}</div>;
  }

  return (
    <div className="savings-container">
      <div className="savings-header">
        <h2>My Savings Account</h2>
        {savings && (
          <div className="staff-info">
            <p>Staff ID: <strong>{savings.staffId}</strong></p>
            <p>Name: <strong>{savings.staffName}</strong></p>
          </div>
        )}
        <div className="savings-balance">
          <h3>Current Balance</h3>
          <p>{formatAmount(savings?.currentBalance || 0)}</p>
          <small>Last Updated: {formatDate(savings?.lastUpdated?.toDate() || new Date())}</small>
        </div>
      </div>

      <div className="transactions-list">
        <h3>Transaction History</h3>
        {transactions.length === 0 ? (
          <p className="no-transactions">No transactions found</p>
        ) : (
          <div className="transactions-table">
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Type</th>
                  <th>Amount</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((transaction) => (
                  <tr key={transaction.id}>
                    <td>{formatDate(transaction.createdAt)}</td>
                    <td className={`transaction-type ${transaction.type}`}>
                      {getTransactionLabel(transaction.type)}
                      {transaction.type === 'transfer_in' && transaction.transferFromName && (
                        <span className="transfer-detail"> from {transaction.transferFromName}</span>
                      )}
                      {transaction.type === 'transfer_out' && transaction.transferToName && (
                        <span className="transfer-detail"> to {transaction.transferToName}</span>
                      )}
                    </td>
                    <td className={`transaction-amount ${transaction.type}`}>
                      {formatAmount(transaction.amount)}
                    </td>
                    <td>{transaction.description || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default StaffSavings; 